import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Badge,
  Button,
  InputGroup,
  Form,
  Modal,
  Card,
  Spinner,
} from "react-bootstrap";
import {
  BsSearch,
  BsEye,
  BsStars,
  BsTelephoneOutbound,
  BsTelephoneFill,
  BsCashStack,
} from "react-icons/bs";
import { toast } from "react-toastify";
import WebService from "../../../Services/WebService";
import HelperService from "../../../Services/HelperService";
import RmAiEmailModal from "../Modals/RmAiEmailModal";
import RmLogActivityModal from "../Modals/RmLogActivityModal";
import RmVoiceCallModal from "../Modals/RmVoiceCallModal";
import PrimePagination from "../../Common/SSPagination/PrimePagination";

const RmInvestors = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loader, setLoader] = useState(false);
  const [detailsLoader, setDetailsLoader] = useState(false);
  const [list, setList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState("");

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [profileDetails, setProfileDetails] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [callUser, setCallUser] = useState<any>(null);

  const openCallModal = (user: any) => {
    setCallUser(user);
    setShowCallModal(true);
  };

  const itemsPerPage = 10;

  useEffect(() => {
    fetchInvestors(keyword, currentPage);
  }, [currentPage]);

  const fetchInvestors = (searchKw: string, page: number = currentPage) => {
    setLoader(true);
    const apiOffset = page - 1;
    const endpoint = `rm-user/list/investor?status=ALL&offset=${apiOffset}&keyword=${searchKw}&country=&from_date=&to_date=&order_by=&sort_by=`;

    WebService.getAPI({ action: endpoint, body: null })
      .then((res: any) => {
        setList(res?.list || []);
        setTotalCount(res?.count || res?.list?.length || 0);
        setLoader(false);
      })
      .catch((error: any) => {
        setLoader(false);
        toast.error(error?.response?.data?.message || "Failed to load investors");
      });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchInvestors(keyword, 1);
  };

  const openViewDetails = (item: any) => {
    const profileId = item?.uuid || item?.id;
    navigate(`/rm/investors/details/${profileId}`);
  };

  const openEmailModal = (user: any) => {
    setSelectedUser(user);
    setShowEmailModal(true);
  };

  const openLogModal = (user: any) => {
    setSelectedUser(user);
    setShowLogModal(true);
  };

  const getSrNo = (index: number) => {
    return (currentPage - 1) * itemsPerPage + index + 1;
  };

  return (
    <div className="page-content pb-5" style={{ minHeight: "calc(100vh - 70px)", background: "#f8f9fa" }}>
      <Container fluid className="px-4 pt-3">
        {/* Header Title */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div>
            <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <BsCashStack style={{ color: "#3fb564" }} /> Investors Directory (View Mode)
            </h4>
            <p className="text-muted font-13 mb-0">
              Browse live investor profiles, compose AI email drafts, and track engagement activities.
            </p>
          </div>
          <Badge pill className="px-3 py-2 font-13 text-white" style={{ backgroundColor: "#3fb564" }}>
            Total Investors: {totalCount}
          </Badge>
        </div>

        {/* Controls */}
        <Row className="g-3 align-items-center mb-4">
          <Col lg={6}>
            <InputGroup className="shadow-xs rounded-pill overflow-hidden border">
              <Form.Control
                placeholder="Search investor name, firm, email..."
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
              <Button style={{ backgroundColor: "#3fb564", borderColor: "#3fb564" }} onClick={handleSearch}>
                <BsSearch /> Search
              </Button>
            </InputGroup>
          </Col>
        </Row>

        {/* Data Table */}
        <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 table-style-1">
              <thead className="bg-light">
                <tr>
                  <th className="py-3 px-3">#</th>
                  <th className="py-3">Investor Name / Email</th>
                  <th className="py-3">Firm / Company</th>
                  <th className="py-3">Mobile No.</th>
                  <th className="py-3">Status</th>
                  <th className="py-3 text-center" style={{ minWidth: "200px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loader ? (
                  <tr>
                    <td colSpan={6} className="text-center py-5">
                      <Spinner animation="border" variant="success" />
                      <p className="text-muted font-13 mt-2">Fetching investors list from server...</p>
                    </td>
                  </tr>
                ) : list && list.length > 0 ? (
                  list.map((item: any, idx: number) => {
                    const userNameStr = item?.users?.name || item?.name || "-";
                    const emailStr = item?.users?.email || item?.email || "-";
                    const firmName = item?.firm_name || item?.company_name || item?.users?.companies?.company_name || "Angel / Individual";
                    const mobile = item?.users?.mobile_number ? `${item?.users?.dial_code || ""}-${item?.users?.mobile_number}` : "-";
                    const statusStr = item?.users?.status || item?.status || "ACTIVE";

                    return (
                      <tr key={idx}>
                        <td className="px-3 font-13 fw-semibold">{getSrNo(idx)}</td>
                        <td>
                          <div
                            className="fw-bold text-dark font-14 text-primary-hover"
                            style={{ cursor: "pointer" }}
                            onClick={() => openViewDetails(item)}
                          >
                            {userNameStr}
                          </div>
                          <div className="text-muted font-12">{emailStr}</div>
                        </td>
                        <td className="fw-semibold text-success font-13">{firmName}</td>
                        <td className="font-13 text-muted">{mobile}</td>
                        <td>
                          <Badge className={HelperService.decideBadgeClass(statusStr)}>
                            {statusStr}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-1.5">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="rounded-circle p-1.5"
                              title="View Details (View Mode)"
                              onClick={() => openViewDetails(item)}
                            >
                              <BsEye size={15} />
                            </Button>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="rounded-circle p-1.5"
                              title="Send Email / AI Draft"
                              onClick={() => openEmailModal(item)}
                            >
                              <BsStars size={15} />
                            </Button>
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="rounded-circle p-1.5"
                              title="Call via 2Chat Voice"
                              onClick={() => openCallModal(item)}
                            >
                              <BsTelephoneFill size={13} />
                            </Button>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="rounded-circle p-1.5 text-dark"
                              title="Log Call"
                              onClick={() => openLogModal(item)}
                            >
                              <BsTelephoneOutbound size={15} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted font-14">
                      No investor profiles found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>

        {/* Pagination */}
        {!loader && totalCount > itemsPerPage && (
          <div className="d-flex justify-content-end me-2">
            <PrimePagination
              totalCount={totalCount}
              itemCountPerPage={itemsPerPage}
              currentPage={currentPage}
              changePage={(page: number) => {
                setCurrentPage(page);
              }}
            />
          </div>
        )}
      </Container>

      {/* View Mode Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title className="font-18 fw-bold text-dark d-flex align-items-center gap-2">
            <BsCashStack className="text-success" /> Investor Details (View Only)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-3">
          {detailsLoader ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="text-muted font-13 mt-2">Loading investor details from server...</p>
            </div>
          ) : (
            <div>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Investor Name</small>
                    <h5 className="fw-bold text-dark mb-0">
                      {profileDetails?.users?.name || selectedUser?.users?.name || selectedUser?.name || "-"}
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Email Address</small>
                    <h5 className="fw-bold text-success mb-0">
                      {profileDetails?.users?.email || selectedUser?.users?.email || selectedUser?.email || "-"}
                    </h5>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Mobile Number</small>
                    <p className="fw-semibold text-dark mb-0">
                      {profileDetails?.users?.mobile_number
                        ? `${profileDetails?.users?.dial_code || ""}-${profileDetails?.users?.mobile_number}`
                        : selectedUser?.users?.mobile_number || "-"}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Investment Firm / Entity</small>
                    <p className="fw-semibold text-dark mb-0">
                      {profileDetails?.investor_business_details?.company_name ||
                        selectedUser?.firm_name ||
                        selectedUser?.company_name ||
                        "-"}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Investment Ticket Range</small>
                    <p className="fw-semibold text-success mb-0">
                      {profileDetails?.investor_business_details?.ticket_size ||
                        profileDetails?.investor_business_details?.investment_amount ||
                        "-"}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded-3">
                    <small className="text-muted font-12 fw-semibold text-uppercase">Preferred Industries / Sectors</small>
                    <p className="fw-semibold text-dark mb-0">
                      {profileDetails?.investor_business_details?.preferred_sectors ||
                        profileDetails?.investor_business_details?.industry_name ||
                        "-"}
                    </p>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="outline-success"
                  onClick={() => {
                    setShowViewModal(false);
                    openEmailModal(selectedUser);
                  }}
                >
                  <BsStars className="me-1" /> Compose AI Mail
                </Button>
                <Button
                  variant="outline-info"
                  onClick={() => {
                    setShowViewModal(false);
                    openCallModal(selectedUser);
                  }}
                >
                  <BsTelephoneFill className="me-1" /> Voice Call
                </Button>
                <Button
                  variant="outline-warning"
                  className="text-dark"
                  onClick={() => {
                    setShowViewModal(false);
                    openLogModal(selectedUser);
                  }}
                >
                  <BsTelephoneOutbound className="me-1" /> Log Interaction
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Action Modals */}
      <RmAiEmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        recipientEmail={selectedUser?.users?.email || selectedUser?.email || ""}
        recipientName={selectedUser?.users?.name || selectedUser?.name || ""}
        userType="INVESTOR"
        userId={selectedUser?.users?.id || selectedUser?.id || selectedUser?.uuid}
      />

      <RmVoiceCallModal
        show={showCallModal}
        onHide={() => setShowCallModal(false)}
        user={callUser}
        userType="INVESTOR"
        onLogCall={(u, dur) => {
          setSelectedUser(u);
          setShowLogModal(true);
        }}
      />

      <RmLogActivityModal
        show={showLogModal}
        onHide={() => setShowLogModal(false)}
        contactName={selectedUser?.users?.name || selectedUser?.name || ""}
        contactEmail={selectedUser?.users?.email || selectedUser?.email || ""}
        userType="INVESTOR"
      />
    </div>
  );
};

export default RmInvestors;
